import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDeferredInitialLoad, useItemSelectionController } from '../../hooks'
import useGetActiveTasks from '../../hooks/useGetActiveTasks'
import { useFeatureFlags } from '../../services/api/feature-flags.hooks'
import Log from '../../services/api/log'
import { useGetOverviewViews } from '../../services/api/overview.hooks'
import { useGetSettings } from '../../services/api/settings.hooks'
import { getOverviewTasksInOrder, isTaskOverviewView } from '../../utils/overview'
import getSortAndFilterSettings from '../../utils/sortAndFilter/getSortAndFilterSettings'
import sortAndFilterItems from '../../utils/sortAndFilter/sortAndFilterItems'
import { TASK_SORT_AND_FILTER_CONFIG } from '../../utils/sortAndFilter/tasks.config'
import { TOverviewItem } from '../../utils/types'

type TOverviewItemWithListId = TOverviewItem & { listId: string }

// returns overview lists with view items sorted and filtered
const useOverviewLists = () => {
    const shouldLoadSecondaryQueries = useDeferredInitialLoad()
    const { data: lists, isLoading: isListsLoading, isSuccess } = useGetOverviewViews()
    const { data: featureFlags, isLoading: areFeatureFlagsLoading } = useFeatureFlags()
    const { data: settings, isLoading: isSettingsLoading } = useGetSettings(shouldLoadSecondaryQueries)
    const { data: activeTasks, isLoading: isActiveTasksLoading } = useGetActiveTasks()
    const navigate = useNavigate()

    // The settings query is deliberately deferred by one paint. Until it is enabled, React Query
    // reports it as idle rather than loading; treating that gap as ready would briefly expose an
    // empty list and lock the Overview accordion seed to no cards.
    const isLoading =
        !shouldLoadSecondaryQueries ||
        isListsLoading ||
        areFeatureFlagsLoading ||
        isSettingsLoading ||
        isActiveTasksLoading

    const sortedAndFilteredLists = useMemo(() => {
        if (
            isListsLoading ||
            areFeatureFlagsLoading ||
            isSettingsLoading ||
            isActiveTasksLoading ||
            !lists ||
            !settings
        )
            return []
        // Saved order is the only order: no client-side resorting, so a new card stays where it was placed.
        return lists
            .filter((list) => featureFlags?.routines || list.type !== 'routine')
            .map((list) => {
                if (list.type === 'due_today') {
                    const viewItems = getOverviewTasksInOrder(list.view_item_ids, activeTasks)
                    return { ...list, view_items: viewItems, total_view_items: viewItems.length }
                }
                if (list.type === 'task_section') {
                    const { selectedSort, selectedSortDirection, selectedFilter } = getSortAndFilterSettings(
                        settings,
                        TASK_SORT_AND_FILTER_CONFIG,
                        list.task_section_id,
                        '_overview'
                    )
                    const viewItems = activeTasks?.filter((task) => list.view_item_ids.includes(task.id)) ?? []
                    const sortedAndFiltered = sortAndFilterItems({
                        items: viewItems,
                        sort: selectedSort,
                        sortDirection: selectedSortDirection,
                        filter: selectedFilter,
                        tieBreakerField: TASK_SORT_AND_FILTER_CONFIG.tieBreakerField,
                    })
                    return { ...list, view_items: sortedAndFiltered, total_view_items: list.view_items.length }
                }
                return list
            })
    }, [lists, isLoading, settings, activeTasks, featureFlags])

    // adds listId to the item so we know which list to navigate to
    const flattenedLists: TOverviewItemWithListId[] = useMemo(
        () =>
            sortedAndFilteredLists.flatMap((list) =>
                isTaskOverviewView(list) ? list.view_items.map((item) => ({ ...item, listId: list.id })) : []
            ),
        [sortedAndFilteredLists]
    )
    const selectItem = useCallback((item: TOverviewItemWithListId) => {
        navigate(`/overview/${item.listId}/${item.id}`)
        Log(`overview_select__/overview/${item.listId}/${item.id}`)
    }, [])
    useItemSelectionController(flattenedLists, selectItem)

    return { lists: sortedAndFilteredLists, isLoading: isLoading, isSuccess: isSuccess, flattenedLists }
}

export default useOverviewLists
